import * as React from 'react';
import { useDispatch } from 'react-redux';

import { useOfferExpertState } from '../../hooks/useOfferExpertState';
import { useOfferTooltipObserver } from '../../hooks/useOfferTooltipObserver';
import { ExpertButton } from './ExpertButton';
import { applyOfferReaction } from '../../../../../../thunks/chatbot';
import { sendChatbotMessage } from '../../../../../../thunks/chatbot/sendChatbotMessage';
import { useApplicationContext } from '../../../../../../utils/applicationContext';
import { useMessageContext } from '../../../../../../utils/messageContext';
import { trackLike } from '../../../../tracking';
import { trackExpertButtonClick } from '../../tracking';
import { IOfferMessage } from '../../types';

interface IExpertButtonContainerProps {
  offer: IOfferMessage;
}

export const ExpertButtonContainer: React.FC<IExpertButtonContainerProps> = ({ offer }) => {
  const {
    messageId,
    messageRef,
    message: { isOutcoming, communicationId },
  } = useMessageContext();
  const dispatch = useDispatch();
  const { config } = useApplicationContext();

  const isChatbotGPTEnabled = config.get<boolean>('frontend-chats.chatbotGPT.Enabled') || false;
  const expertButtonContent = config.get<{ title: string; sendText: string }>('frontend-chats.expertSearchButton');

  const { reaction, isExpertEnabled } = useOfferExpertState(offer);
  const { offerMessageTooltipState, rootRef, hideTooltip } = useOfferTooltipObserver({
    offer,
    isExpertEnabled,
    isChatbotChat: true,
    isOutcoming,
    messageRef,
  });

  const handleExpertButtonClick = React.useCallback(() => {
    trackExpertButtonClick(offer.realtyOfferId, communicationId ?? undefined, offer.position ?? undefined);
    dispatch(sendChatbotMessage(expertButtonContent?.sendText || '', offer.realtyOfferId, messageId));
  }, [offer, dispatch, expertButtonContent, messageId, communicationId]);

  const handleLikeClick = React.useCallback(() => {
    const nextReaction = reaction !== 'like' ? 'like' : undefined;

    trackLike(offer.realtyOfferId, nextReaction, communicationId ?? undefined, offer.position ?? undefined);
    dispatch(
      applyOfferReaction({
        offerId: offer.realtyOfferId,
        reaction: nextReaction,
      }),
    );
  }, [offer, reaction, dispatch, communicationId]);

  if (!isChatbotGPTEnabled || !isExpertEnabled || isOutcoming) {
    return null;
  }

  return (
    <ExpertButton
      expertButtonContent={expertButtonContent}
      visibleTooltip={offerMessageTooltipState?.offerId === offer.realtyOfferId}
      tooltipPortal={rootRef}
      setCloseTooltip={hideTooltip}
      onExpertButton={handleExpertButtonClick}
      onLike={handleLikeClick}
      reaction={reaction}
    />
  );
};
